/**
 * @file: index.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { AppleRealUserStatus } from '@euler/functions/useAppleSignIn';
import { AuthenticatedUserInfo } from '@euler/model/AuthenticatedUserInfo';
import { IdType } from '@euler/model/common';
import { PlatformUser, PlatformUserApp } from '@euler/model/entity';

export enum AuthProviderType {
  Password = 'password',
  Phone = 'phone',
  Weixin = 'weixin',
  AppleSignIn = 'appleSignIn',
  Qrcode = 'qrcode',
  TrustedAuthorization = 'trustedAuthorization',
  TokenExchange = 'tokenExchange',
}

export const AuthProviderTypeSet = new Set([
  AuthProviderType.Password,
  AuthProviderType.Phone,
  AuthProviderType.Weixin,
  AuthProviderType.AppleSignIn,
  AuthProviderType.Qrcode,
  AuthProviderType.TrustedAuthorization,
  AuthProviderType.TokenExchange,
]);

export type PasswordAuthProvider = {
  type: AuthProviderType.Password;
  userName: string;
  password: string;
};

export type QrcodeAuthProvider = {
  type: AuthProviderType.Qrcode;
  code: string;
};

export type TrustedAuthorizationAuthProvider = {
  type: AuthProviderType.TrustedAuthorization;
  authToken: string;
};

export type TokenExchangeAction =
  | {
      type: 'silent-login';
      extra: string;
    }
  | {
      type: 'app-targeting';
      appId: IdType;
    };

export type TokenExchangeAuthProvider = {
  type: AuthProviderType.TokenExchange;
  authToken: string;
  action: TokenExchangeAction;
};

export type PhoneAuthProvider = {
  type: AuthProviderType.Phone;
  phone: string;
  ticket: string;
  verifyCode: string;
};

export type WeixinAuthProvider = {
  type: AuthProviderType.Weixin;
  appId: string;
  nonce: string;
  credential:
    | { type: 'code'; code: string; uid?: string }
    | {
        type: 'phone';
        phone: string;
        ticket: string;
        verifyCode: string;
      };
};

export type AppleSignInUserInfo = {
  email: string | null;
  realUserStatus: AppleRealUserStatus;
};

export type AppleSignInAuthProvider = {
  type: AuthProviderType.AppleSignIn;
  appId: string;
  appleUserId: string;
  credential:
    | {
        type: 'token';
        token: string;
        uid?: string;
        nonce: string;
        extra: AppleSignInUserInfo;
      }
    | {
        type: 'phone';
        phone: string;
        ticket: string;
        verifyCode: string;
      };
};

export type AuthProvider =
  | PasswordAuthProvider
  | PhoneAuthProvider
  | WeixinAuthProvider
  | AppleSignInAuthProvider
  | QrcodeAuthProvider
  | TrustedAuthorizationAuthProvider
  | TokenExchangeAuthProvider;

export type AuthenticatorNextActionApp = PlatformUserApp & {
  orgName?: string;
  orgShortName?: string;
  storeName?: string;
};

export type AuthenticatorNextAction =
  | {
      type: 'federation-continuation';
    }
  | {
      type: 'app-selection';
      apps: AuthenticatorNextActionApp[];
    };

export type AuthenticatorResult<T extends AuthProvider> = {
  user: PlatformUser;
  token: string;
  refreshToken?: string;
  refreshTokenExpiresIn?: number;
  extra?: any;
  expiresIn?: number;
  results: AuthenticatedUserInfo;
  nextAction?: AuthenticatorNextAction;
} & (T extends { type: AuthProviderType.Password }
  ? { requiresChangePassword: boolean }
  : // eslint-disable-next-line @typescript-eslint/ban-types
    {});
